import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SVGViewer.css';
import svgFile from '../assets/images/CH-Umrisse.svg';
import pinFile from '../assets/images/Pin.svg';
import bgRasterFile from '../assets/images/BG-Raster.svg';

const SVGViewer = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [isAnyPinHovered, setIsAnyPinHovered] = useState(false);

  const originalWidth = 1613.89;
  const originalHeight = 1141.69;

  const [viewBox, setViewBox] = useState({
    x: 0,
    y: 0,
    width: originalWidth,
    height: originalHeight
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, viewX: 0, viewY: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [svgContent, setSvgContent] = useState('');
  const [bgRasterContent, setBgRasterContent] = useState('');
  const [hoveredPin, setHoveredPin] = useState(null);
  const containerRef = useRef(null);

  // Momentum/inertia tracking
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastPositionRef = useRef({ x: 0, y: 0, time: 0 });
  const momentumRef = useRef(null);
  const zoomCenterRef = useRef({ x: 0, y: 0 });

  // Define all pins with their positions and names
  const pins = [
    { name: 'PinElisabeth', displayName: 'Elisabeth Joris', x: originalWidth * 0.58, y: originalHeight * 0.31 },
    { name: 'PinLiselotte', displayName: 'Liselotte Weiss', x: originalWidth * 0.38, y: originalHeight * 0.28 },
    { name: 'PinFranziska', displayName: 'Franziska Rogger', x: originalWidth * 0.42, y: originalHeight * 0.45 },
    { name: 'PinGabrielle', displayName: 'Gabrielle Nanchen', x: originalWidth * 0.35, y: originalHeight * 0.75 },
    { name: 'PinNicole', displayName: 'Nicole Viguet', x: originalWidth * 0.21, y: originalHeight * 0.65 },
    { name: 'PinYolanda', displayName: 'Yolanda Zuber', x: originalWidth * 0.63, y: originalHeight * 0.24 },
    { name: 'PinRita', displayName: 'Rita Cathomas-Bearth', x: originalWidth * 0.71, y: originalHeight * 0.47 },
    { name: 'PinSonja', displayName: 'Sonja Crivelli', x: originalWidth * 0.64, y: originalHeight * 0.83 }
  ];

  // Load Swiss map SVG content from file
  useEffect(() => {
    const loadSVG = async () => {
      try {
        const response = await fetch(svgFile);
        const text = await response.text();

        // Extract the path data from the SVG
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(text, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (svgElement) {
          setSvgContent(svgElement.innerHTML);
        }
      } catch (error) {
        console.error('Error loading Swiss map SVG:', error);
      }
    };

    loadSVG();
  }, []);

  // Load BG-Raster SVG content from file
  useEffect(() => {
    const loadBgRaster = async () => {
      try {
        const response = await fetch(bgRasterFile);
        const text = await response.text();

        // Extract the raster content
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(text, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        if (svgElement) {
          setBgRasterContent(svgElement.innerHTML);
        }
      } catch (error) {
        console.error('Error loading BG-Raster SVG:', error);
      }
    };

    loadBgRaster();
  }, []);

  // Monitor container size changes and auto-fit
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    const timer = setTimeout(() => {
      updateSize();
      fitToContainer();
      setIsLoaded(true);
    }, 100);

    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timer);
    };
  }, [svgContent, bgRasterContent]);

  //Scroll listener for mobile
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsHeaderScrolled(scrollTop > 50); // Trigger after 50px scroll
    };

    const isMobile = window.innerWidth <= 480;
    if (isMobile) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Convert screen coordinates to SVG coordinates
  const screenToSVG = useCallback((screenX, screenY) => {
    if (!containerRef.current) return { x: 0, y: 0 };

    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = (screenX - rect.left) / rect.width;
    const relativeY = (screenY - rect.top) / rect.height;

    return {
      x: viewBox.x + relativeX * viewBox.width,
      y: viewBox.y + relativeY * viewBox.height
    };
  }, [viewBox]);

  // Drag momentum animation
  const animateDragMomentum = useCallback(() => {
    const friction = 0.97;
    const threshold = 0.1;

    velocityRef.current.x *= friction;
    velocityRef.current.y *= friction;

    if (Math.abs(velocityRef.current.x) < threshold && Math.abs(velocityRef.current.y) < threshold) {
      velocityRef.current = { x: 0, y: 0 };
      return;
    }

    setViewBox(prev => ({
      ...prev,
      x: prev.x + velocityRef.current.x,
      y: prev.y + velocityRef.current.y
    }));

    momentumRef.current = requestAnimationFrame(animateDragMomentum);
  }, []);

  // Handle mouse down - start dragging
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);

    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
    }
    velocityRef.current = { x: 0, y: 0 };

    const now = Date.now();
    lastPositionRef.current = { x: e.clientX, y: e.clientY, time: now };

    setDragStart({
      x: e.clientX,
      y: e.clientY,
      viewX: viewBox.x,
      viewY: viewBox.y
    });
  }, [viewBox.x, viewBox.y]);

  // Handle mouse move - pan the viewBox and track velocity
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const deltaX = (e.clientX - dragStart.x) / rect.width;
    const deltaY = (e.clientY - dragStart.y) / rect.height;

    const now = Date.now();
    const timeDelta = now - lastPositionRef.current.time;

    if (timeDelta > 0) {
      const positionDeltaX = e.clientX - lastPositionRef.current.x;
      const positionDeltaY = e.clientY - lastPositionRef.current.y;

      velocityRef.current.x = -(positionDeltaX / rect.width) * viewBox.width * 0.016;
      velocityRef.current.y = -(positionDeltaY / rect.height) * viewBox.height * 0.016;
    }

    lastPositionRef.current = { x: e.clientX, y: e.clientY, time: now };

    setViewBox(prev => ({
      ...prev,
      x: dragStart.viewX - deltaX * prev.width,
      y: dragStart.viewY - deltaY * prev.height
    }));
  }, [isDragging, dragStart, viewBox.width, viewBox.height]);

  // Handle mouse up - start momentum
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);

    if (Math.abs(velocityRef.current.x) > 0.1 || Math.abs(velocityRef.current.y) > 0.1) {
      momentumRef.current = requestAnimationFrame(animateDragMomentum);
    }
  }, [animateDragMomentum]);

  // Handle wheel
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    const svgPoint = screenToSVG(e.clientX, e.clientY);
    const zoomDelta = e.deltaY > 0 ? 0.1 : -0.1;
    const zoomFactor = 1 + zoomDelta;

    const currentViewBox = viewBox;
    const newWidth = Math.max(10, Math.min(originalWidth * 10, currentViewBox.width * zoomFactor));
    const newHeight = Math.max(10, Math.min(originalHeight * 10, currentViewBox.height * zoomFactor));

    const newX = svgPoint.x - (svgPoint.x - currentViewBox.x) * (newWidth / currentViewBox.width);
    const newY = svgPoint.y - (svgPoint.y - currentViewBox.y) * (newHeight / currentViewBox.height);

    setViewBox({
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight
    });

  }, [viewBox, screenToSVG]);

  // Cleanup animation frames on unmount
  useEffect(() => {
    return () => {
      if (momentumRef.current) {
        cancelAnimationFrame(momentumRef.current);
      }
    };
  }, []);

  // Fit to container by adjusting viewBox
  const fitToContainer = () => {
    if (!containerSize.width || !containerSize.height) return;

    const containerAspect = containerSize.width / containerSize.height;
    const svgAspect = originalWidth / originalHeight;

    let newWidth, newHeight;

    if (containerAspect > svgAspect) {
      newHeight = originalHeight;
      newWidth = originalHeight * containerAspect;
    } else {
      newWidth = originalWidth;
      newHeight = originalWidth / containerAspect;
    }

    newWidth *= 1.1;
    newHeight *= 1.1;

    const remOffset = 5 * 16;
    const viewBoxOffset = (remOffset / containerSize.height) * newHeight;

    setViewBox({
      x: (originalWidth - newWidth) / 2,
      y: (originalHeight - newHeight) / 2 - viewBoxOffset,
      width: newWidth,
      height: newHeight
    });
  };

  // Button handlers
  const handleProtagonistinnen = () => {
    console.log('Protagonistinnen clicked');
    navigate('/');
  };

  const handleEreignisse = () => {
    console.log('Ereignisse clicked');
    navigate('/ereignisse');
  };

  // Pin handlers
  const handlePinClick = (pinName) => {
    console.log(`${pinName} clicked`);
    navigate(`/person/${pinName}`);
  };

  const handlePinMouseEnter = (pinName) => {
    setHoveredPin(pinName);
    setIsAnyPinHovered(true);
  };

  const handlePinMouseLeave = () => {
    setHoveredPin(null);
    setIsAnyPinHovered(false);
  };

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    // Prevent iOS Safari from scrolling the body
    const preventDefault = (e) => {
      if (e.target.closest('.protagonists-content')) {
        // Allow scrolling only within the protagonists content
        return;
      }
      e.preventDefault();
    };

    const preventDefaultForScrollKeys = (e) => {
      // Prevent arrow keys, space, page up/down from scrolling the main body
      if ([32, 33, 34, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) > -1) {
        e.preventDefault();
      }
    };

    // Only apply on mobile
    if (window.innerWidth <= 480) {
      document.addEventListener('touchmove', preventDefault, { passive: false });
      document.addEventListener('keydown', preventDefaultForScrollKeys, { passive: false });

      // Prevent overscroll
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';

      return () => {
        document.removeEventListener('touchmove', preventDefault);
        document.removeEventListener('keydown', preventDefaultForScrollKeys);
        document.body.style.overscrollBehavior = '';
        document.documentElement.style.overscrollBehavior = '';
      };
    }
  }, []);

  return (
    <div className="svg-viewer">
      <div className="header">
        {/* Mobile burger menu button */}
        <button
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label="Menu"
        >
          <div className={`burger-line ${isMobileMenuOpen ? 'active' : ''}`}></div>
          <div className={`burger-line ${isMobileMenuOpen ? 'active' : ''}`}></div>
          <div className={`burger-line ${isMobileMenuOpen ? 'active' : ''}`}></div>
        </button>

        <div className="title-section">
          <h1 className="title">
            <img
              src="/assets/images/Echo_1971_Logo.png"
              alt="Echo 1971"
            />
            <span className="sr-only">Echo 1971</span>
          </h1>
          <p className="middle-text">Echo 1971 ist ein dokumentarisches Archiv über Frauenstimmrecht, Erinnerung und gesellschaftlichen Wandel in der Schweiz. Interviews, historische Ereignisse und persönliche Geschichten verbinden Vergangenheit und Gegenwart.</p>
        </div>

        {/* Desktop header buttons */}
        <div className="header-buttons desktop-only">
          <button onClick={handleProtagonistinnen} className="btn protagonistinnen-btn">
            Protagonistinnen
          </button>
          <button onClick={handleEreignisse} className="btn">
            Ereignisse
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}>
          <div className={`mobile-menu-content ${isMobileMenuOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
            <button
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close Menu"
            >
              <div className="burger-line active"></div>
              <div className="burger-line active"></div>
              <div className="burger-line active"></div>
            </button>
            <button
              onClick={() => { handleProtagonistinnen(); closeMobileMenu(); }}
              className="mobile-menu-item protagonistinnen-btn"
            >
              Protagonistinnen
            </button>
            <button
              onClick={() => { handleEreignisse(); closeMobileMenu(); }}
              className="mobile-menu-item ereignisse-btn"
            >
              Ereignisse
            </button>
          </div>
        </div>
      )}

      <div className="footer">
        <span
          className="footer-left"
          onClick={() => navigate('/impressum')}
          style={{ cursor: 'pointer' }}
        >
          Impressum
        </span>
        <span
          className="footer-right"
          onClick={() => window.open('https://alexaweiss.ch', '_blank')}
        >
          Ein Projekt von Alexa Weiss
        </span>
      </div>

      {/* Background raster - hidden on mobile */}
      {bgRasterContent && (
        <div className="bg-raster-container desktop-only">
          <svg
            className="bg-raster-svg"
            width="100%"
            height="100%"
            viewBox="0 0 2562.75 2675.47"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
            dangerouslySetInnerHTML={{ __html: bgRasterContent }}
          />
        </div>
      )}

      {/* Desktop map view */}
      <div
        ref={containerRef}
        className={`svg-container desktop-only ${isDragging ? 'dragging' : ''} ${!isLoaded ? 'loading' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        {svgContent && (
          <>
            <div className={`map-instruction-text ${isAnyPinHovered ? 'fade-out' : ''}`}>
              Pin auswählen, um persönliche Geschichten zu lesen
            </div>

            <svg
              className="ch-map-svg"
              width="100%"
              height="100%"
              viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
            >
              <defs>
                <filter id="pin-outline-black" x="-20%" y="-20%" width="140%" height="140%">
                  <feMorphology operator="dilate" radius="2" in="SourceGraphic" result="dilated" />
                  <feFlood floodColor="#000000" floodOpacity="1" result="black" />
                  <feComposite in="black" in2="dilated" operator="in" result="outline" />
                  <feMerge>
                    <feMergeNode in="outline" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <g dangerouslySetInnerHTML={{ __html: svgContent }} />

              {pins.map((pin, index) => (
                <g
                  key={pin.name}
                  className="pin-container"
                  transform={`translate(${pin.x}, ${pin.y})`}
                  onClick={() => handlePinClick(pin.name)}
                  onMouseEnter={() => handlePinMouseEnter(pin.name)}
                  onMouseLeave={handlePinMouseLeave}
                  style={{
                    cursor: 'pointer',
                    filter: hoveredPin === pin.name ? 'url(#pin-outline-black)' : 'none',
                    transition: 'filter 0.3s ease'
                  }}
                >
                  <circle
                    cx="0"
                    cy="-90"
                    r="35"
                    fill="transparent"
                    className="pin-hitbox"
                  />
                  <image
                    href={pinFile}
                    x="-50"
                    y="-100"
                    width="100"
                    height="100"
                    className="pin-image"
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              ))}
            </svg>
          </>
        )}
      </div>

      {/* Mobile protagonists list */}
      <div className="mobile-protagonists-list mobile-only">
        <div className="protagonists-content">
          <h2 className="protagonists-title">Protagonistinnen</h2>
          <div className="protagonists-grid">
            {pins.map((pin) => (
              <button
                key={pin.name}
                className="protagonist-card"
                onClick={() => handlePinClick(pin.name)}
              >
                <span className="protagonist-name">{pin.displayName}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {!isLoaded && (
        <div className="loading-overlay desktop-only">
        </div>
      )}
    </div>
  );
};

export default SVGViewer;